import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { getCurrency } from "@repo/utils";
import { useStores } from "@hooks";
import {
  getCouponQuery,
  getCurrentSubscriptionQuery,
  getTeamContractorsQuery,
  refreshUserDataQuery
} from "@query";
import { parsePackageName } from "@utils";
import { PageTitle } from "@components";
import Grid from "@material-ui/core/Grid";
import Container from "@material-ui/core/Container";
import { ContractorsTable } from "../Team/components";
import { TeamView } from "../Team/TeamView";
import { SectionCards, SectionPayments, SectionPlan } from "./components";

export const Subscription = observer(() => {
  const { userStore } = useStores();
  const { t } = useTranslation();

  const [ isLoading, setSubLoading ] = useState(true);
  const [ areContractorsLoading, setContractorsLoading ] = useState(true);
  const [ subscription, setSubscription ] = useState(null);
  const [ coupon, setCoupon ] = useState(null);
  const [ contractors, setContractors ] = useState(null);

  const loadSubscription = async () => {
    setSubLoading(true);
    const c = await getCouponQuery();
    setCoupon(c?.code);
    const s = await getCurrentSubscriptionQuery();
    if (s?.name) {
      userStore.updateSubscriptionData(
        parsePackageName(s.name),
        s.name,
        s.users,
        s.estimates,
        s.trialTo,
        s.isTrialing ? "try" : "buy"
      );
    } else if(s) {
      s.name = t("plan.default")
    }
    setSubscription(s);
    setSubLoading(false);
  };
  
  const loadContractorData = async () => {
    setContractorsLoading(true);
    const res = await getTeamContractorsQuery();
    setContractors(res);
    setContractorsLoading(false);
  };

  useEffect(() => {
    (async () => {
      const data = await refreshUserDataQuery();
      await userStore.saveUserData(data);
      await loadSubscription();
      await loadContractorData();
    })();
  }, []);

  const packageName = useMemo(() => (
    parsePackageName(subscription?.name || userStore.subscriptionInfo.premium)
  ), [subscription, userStore.data]);

  const name = useMemo(() => (
    t(`plans.${packageName}`)
  ), [packageName]);

  const currency = useMemo(() => (
    subscription?.currency || getCurrency("USD")
  ), [subscription]);
  
  return (
    <>
      <PageTitle />
      <Container>
        <Grid
          item
          container
          className="rounded-2 h-max mb-6 border-solid bg-secondary border-light"
          style={{ minHeight: 260 }}
          spacing={0}
        >
          <SectionPlan
            name={name}
            packageName={packageName}
            isLoading={isLoading}
            hasSubscription={Boolean(subscription)}
            currency={currency}
            duration={subscription?.duration}
            price={subscription?.price}
            discountPrice={subscription?.discountPrice}
            estimates={subscription?.estimates}
            users={subscription?.users}
            coupon={coupon}
            setCoupon={setCoupon}
          />
          <SectionCards
            coupon={coupon}
            isSubscriptionLoading={isLoading}
            hasSubscription={Boolean(subscription)}
            currency={currency}
            subscriptionEnd={subscription?.to}
            subscriptionEndsDate={subscription?.subscriptionEnds}
            subscriptionPrice={
              subscription?.discountPrice || subscription?.price
            }
            defaultPaymentMethod={subscription?.defaultPaymentMethod}
            lastPaymentPeriod={subscription?.lastPeriod}
          />
          <SectionPayments />
        </Grid>
        <Grid
          item container
          className="rounded-2 h-max mb-6 p-4 border-solid bg-secondary border-light"
        >
          <TeamView useTableTitle />
        </Grid>
        <Grid
          item container
          className="rounded-2 h-max mb-6 p-4 border-solid bg-secondary border-light"
        >
          <ContractorsTable
            useTableTitle
            isLoading={areContractorsLoading}
            contractors={contractors}
            reloadData={loadContractorData}
          />
        </Grid>
      </Container>
    </>
  );
});